import { useState, useEffect } from 'react';
import { hipaaAPI, adminAPI } from '../../lib/api';
import { 
  Shield, 
  CheckCircle, 
  Clock,
  User,
  Calendar,
  MagnifyingGlass,
  Funnel,
  CaretDown,
  CaretUp,
  FileText,
  Warning,
  DownloadSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AdminConsentsPage() {
  const [consents, setConsents] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Get all users who are patients
      const usersRes = await adminAPI.getUsers();
      const patientUsers = usersRes.data.filter(u => u.role === 'patient');
      setPatients(patientUsers);

      // Get all consents
      const consentsRes = await hipaaAPI.getAllConsents();
      setConsents(consentsRes.data || []);
    } catch (error) {
      console.error('Error loading consents:', error);
      toast.error('Failed to load consent data');
    } finally {
      setLoading(false);
    }
  };

  const consentTypes = [
    'privacy_notice',
    'treatment_consent',
    'release_information',
    'telehealth_consent',
    'financial_responsibility',
    'medication_records_release',
    'medical_records_release'
  ];

  const formatConsentType = (type) => {
    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const isExpired = (signedDate) => {
    if (!signedDate) return false;
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return new Date(signedDate) < oneYearAgo;
  };

  const isExpiringSoon = (signedDate) => {
    if (!signedDate) return false;
    const elevenMonthsAgo = new Date();
    elevenMonthsAgo.setMonth(elevenMonthsAgo.getMonth() - 11);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const signed = new Date(signedDate);
    return signed < elevenMonthsAgo && signed >= oneYearAgo;
  };

  const getExpiryDate = (signedDate) => {
    if (!signedDate) return null;
    const expiry = new Date(signedDate);
    expiry.setFullYear(expiry.getFullYear() + 1);
    return expiry;
  };

  // Group consents by patient
  const getPatientConsents = (patientId) => {
    return consents.filter(c => c.patient_id === patientId);
  };

  // Filter and search patients
  const filteredPatients = patients.filter(patient => {
    const patientConsents = getPatientConsents(patient.id);
    const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || 
                          patient.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (filterStatus === 'all') return true;
    if (filterStatus === 'complete') {
      return consentTypes.every(type => 
        patientConsents.some(c => c.consent_type === type && !isExpired(c.signed_at))
      );
    }
    if (filterStatus === 'incomplete') {
      return !consentTypes.every(type => 
        patientConsents.some(c => c.consent_type === type && !isExpired(c.signed_at))
      );
    }
    if (filterStatus === 'expired') {
      return patientConsents.some(c => isExpired(c.signed_at));
    }
    if (filterStatus === 'expiring') {
      return patientConsents.some(c => isExpiringSoon(c.signed_at));
    }
    return true;
  });

  // Sort patients
  const sortedPatients = [...filteredPatients].sort((a, b) => {
    if (sortBy === 'name') {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    }
    if (sortBy === 'date') {
      const consentsA = getPatientConsents(a.id);
      const consentsB = getPatientConsents(b.id);
      const latestA = consentsA.length > 0 ? Math.max(...consentsA.map(c => new Date(c.signed_at))) : 0;
      const latestB = consentsB.length > 0 ? Math.max(...consentsB.map(c => new Date(c.signed_at))) : 0;
      return sortOrder === 'asc' ? latestA - latestB : latestB - latestA;
    }
    return 0;
  });

  const getConsentStatus = (patientId, consentType) => {
    const consent = consents.find(c => c.patient_id === patientId && c.consent_type === consentType);
    if (!consent) return { status: 'missing', consent: null };
    if (isExpired(consent.signed_at)) return { status: 'expired', consent };
    if (isExpiringSoon(consent.signed_at)) return { status: 'expiring', consent };
    return { status: 'valid', consent };
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'valid':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1"><CheckCircle size={12} /> Valid</span>;
      case 'expired':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1"><Warning size={12} /> Expired</span>;
      case 'expiring':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1"><Clock size={12} /> Expiring Soon</span>;
      case 'missing':
        return <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded-full">Not Signed</span>;
      default:
        return null;
    }
  };

  const stats = {
    totalPatients: patients.length,
    completeConsents: filteredPatients.filter(p => 
      consentTypes.every(type => getConsentStatus(p.id, type).status === 'valid')
    ).length,
    expiredConsents: consents.filter(c => isExpired(c.signed_at)).length,
    expiringSoon: consents.filter(c => isExpiringSoon(c.signed_at)).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Signed Consents</h1>
          <p className="text-slate-500">View and manage all patient consent forms</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.totalPatients}</p>
              <p className="text-sm text-slate-500">Total Patients</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.completeConsents}</p>
              <p className="text-sm text-slate-500">Fully Compliant</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.expiringSoon}</p>
              <p className="text-sm text-slate-500">Expiring Soon</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Warning size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.expiredConsents}</p>
              <p className="text-sm text-slate-500">Expired</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-clinical p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by patient name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Funnel size={18} className="text-slate-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Status</option>
                <option value="complete">Fully Compliant</option>
                <option value="incomplete">Incomplete</option>
                <option value="expired">Has Expired</option>
                <option value="expiring">Expiring Soon</option>
              </select>
            </div>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [by, order] = e.target.value.split('-');
                setSortBy(by);
                setSortOrder(order);
              }}
              className="border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
            >
              <option value="date-desc">Latest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
            </select>
          </div>
        </div>
      </div>

      {/* Patient Consents List */}
      <div className="card-clinical overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-900">Patient Consent Records ({sortedPatients.length})</h3>
        </div>

        {sortedPatients.length === 0 ? (
          <div className="p-8 text-center">
            <Shield size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No patients found matching your criteria</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sortedPatients.map((patient) => {
              const patientConsents = getPatientConsents(patient.id);
              const isExpanded = expandedPatient === patient.id;
              const validCount = consentTypes.filter(type => getConsentStatus(patient.id, type).status === 'valid').length;
              const totalCount = consentTypes.length;
              
              return (
                <div key={patient.id} data-testid={`patient-consent-${patient.id}`}>
                  {/* Patient Row */}
                  <div 
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => setExpandedPatient(isExpanded ? null : patient.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-100 rounded-full">
                          <User size={20} className="text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{patient.first_name} {patient.last_name}</p>
                          <p className="text-sm text-slate-500">{patient.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-900">{validCount}/{totalCount} Signed</p>
                          <div className="w-24 h-2 bg-slate-200 rounded-full mt-1">
                            <div 
                              className={`h-full rounded-full ${validCount === totalCount ? 'bg-green-500' : validCount > 0 ? 'bg-amber-500' : 'bg-slate-300'}`}
                              style={{ width: `${(validCount / totalCount) * 100}%` }}
                            />
                          </div>
                        </div>
                        {isExpanded ? <CaretUp size={20} className="text-slate-400" /> : <CaretDown size={20} className="text-slate-400" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-slate-50">
                      <div className="grid gap-3">
                        {consentTypes.map((type) => {
                          const { status, consent } = getConsentStatus(patient.id, type);
                          return (
                            <div 
                              key={type} 
                              className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                            >
                              <div className="flex items-center gap-3">
                                <FileText size={18} className="text-slate-400" />
                                <div>
                                  <p className="font-medium text-slate-900">{formatConsentType(type)}</p>
                                  {consent && (
                                    <p className="text-xs text-slate-500">
                                      Signed: {new Date(consent.signed_at).toLocaleDateString()} 
                                      {' | '}
                                      Expires: {getExpiryDate(consent.signed_at)?.toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(status)}
                                {consent && (
                                  <button 
                                    className="p-1 hover:bg-slate-100 rounded"
                                    title="View consent"
                                  >
                                    <DownloadSimple size={16} className="text-slate-400" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
